import { Image, Menu } from "antd";
import Button from "../../components/ui/button";
import { Mail, PhoneCall, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LeadFormComp } from "./LeadFormComp";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { routes } from "../../Routes";
import { getUTMs } from "../../Utils/utm";
import LanguageSwitcher from "../../components/language-switcher";
import { getUserOnboarding } from "../../API  Functions/DashboardFunctions";
import { store } from "../../Redux/store";
import Navbar from "./Navbar";

interface ValueCardProps {
  img: string;
  title: string;
  description: string;
  gradientFrom: string;
  gradientTo: string;
}

const ValueCard: React.FC<ValueCardProps> = ({
  img,
  title,
  description,
  gradientFrom,
  gradientTo,
}) => {
  return (
    <div
      className="rounded-3xl p-3 md:p-8 flex gap-6 items-center border-[1px] border-gray-100"
      style={{
        background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
      }}
    >
      <div className="flex-shrink-0">
        {/* <img src={img} alt={title} width={74} className="w-34 object-cover" /> */}
        <Image
          src={img}
          alt={title}
          width={80}
          height={80}
          className="object-cover"
          preview={false}
        />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-foreground mb-3">{title}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed font-light">
          {description}
        </p>
      </div>
    </div>
  );
};

const AboutUs = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [modalTitle, setModalTitle] = useState(t("Talk to Sales"));
  const [showModal, setShowModal] = useState(false);
  const [utms, setUtms] = useState({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = store.getState();
  const user_id = login.user;
  const is_retailer = login.user?.result?.is_retailer;
  const company_id = login.user?.result?.company_id;
  const [onboardingDone, setOnboardingDone] = useState(null); // null = loading
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const data = getUTMs();
    Object.keys(data).forEach((key) => data[key]);

    setUtms(data);
  }, []);
  useEffect(() => {
    const checkScreen = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreen();
    window.addEventListener("resize", checkScreen);

    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  useEffect(() => {
    const fetchOnboarding = async () => {
      if (company_id) {
        const result = await CheckUserOnboarding(company_id);
        setOnboardingDone(result);
      }
    };
    if (company_id) {
      fetchOnboarding();
    }
  }, [company_id]);

  const CheckUserOnboarding = async (id) => {
    try {
      const res1 = await getUserOnboarding(id);

      if (res1?.result[0]?.status_code === 200) {
        let keys = res1?.result[0];

        return (
          keys?.update_inventory &&
          keys?.cashier_setup &&
          keys?.weighing_scale &&
          keys?.add_user &&
          keys?.delivery_company
        );
      }
      return false;
    } catch (error) {
      console.error("Error fetching KPI data:", error);
      return false;
    }
  };
  const openContactUsModal = () => {
    setModalTitle(t("Contact Us"));
    setShowModal(true);
  };
  const formSubmit = () => {
    navigate(routes.thankyou);
  };
  const valuesData = [
    {
      img: require("../../assets/AboutUs/UnityInDiversity.webp"),
      title: "Unity in Diversity",
      description:
        "We highly value all cultures and backgrounds, respecting everyone's valuable, unique contributions.",
      gradientFrom: "#F5F5F5",
      gradientTo: "#F5FFF5",
    },
    {
      img: require("../../assets/AboutUs/PeopleFirst.webp"),
      title: "People First",
      description:
        "We always put our employees, customers, and communities at the top of everything we do.",
      gradientFrom: "#F9F9FB",
      gradientTo: "#F5F9FF",
    },
    {
      img: require("../../assets/AboutUs/FailFast.webp"),
      title: "Fail Fast",
      description:
        "Learning quickly from small, safe mistakes helps us create great, innovative ideas faster.",
      gradientFrom: "#F9F9FA",
      gradientTo: "#F5F5FD",
    },
    {
      img: require("../../assets/AboutUs/DriveChange.webp"),
      title: "Drive Change",
      description:
        "We constantly look for new, better ways to dramatically improve the global retail market.",
      gradientFrom: "#FAFAF9",
      gradientTo: "#FFF5EB",
    },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="relative overflow-hidden bg-primaryBlue pt-[125px] pb-28 md:pb-32 md:pt-[150px]">
        <div className="flex flex-col text-center w-full justify-center items-center h-full md:h-[95%]">
          <h1 className="text-3xl md:text-5xl font-semibold text-white md:mb-4 px-12 md:px-0">
            About Us
          </h1>

          <p className="text-white/90 text-sm md:text-lg leading-relaxed font-light px-12 md:w-[39%] md:px-0">
            All tools connected. From online orders → store billing → delivery →
            fulfilment → settlement.
          </p>
        </div>

        {/* Bottom Curve */}
        <div className="absolute bottom-[-1px] left-0 w-full leading-none">
          <svg
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            className="block w-full h-[90px]"
          >
            <path
              d="M0,70C360,110,1080,10,1440,55L1440,120L0,120Z"
              fill="#ffffff"
            />
          </svg>
        </div>
      </div>
      <section className="py-10 md:py-16 px-6 md:px-12 bg-white pt-2 md:pt-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-2">
                {t("Who we are?")}
                {/* <div className=""> */}
                {/* <img
                  src={YellowUnderLine}
                  alt="myGINNE"
                  className="w-30 object-cover"
                /> */}
                {/* </div> */}
              </h2>

              <p className="text-textColor text-base md:text-lg mt-6 leading-relaxed font-light">
                {t("Founded in")}{" "}
                <span className="font-semibold text-foreground">2022</span>,
                {t(
                  "myGINNE is dedicated to driving digital transformation in Egypt's retail sector. We empower local businesses with technology to streamline their operations, while delivering smarter, more convenient shopping experiences for consumers. Our goal is simple: to leverage innovation to unlock the full potential of local retail.",
                )}
              </p>
            </div>

            <div className="flex justify-center lg:justify-end">
              {/* <img
                src={whoWeAreImage}
                alt="myCINNE team member with branded flags"
                className="w-full max-w-md lg:max-w-lg object-cover"
              /> */}
              <Image
                src={require("../../assets/AboutUs/Image.webp")}
                alt="myGINNE team member with branded flags"
                preview={false}
                className="w-full max-w-md lg:max-w-lg object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section
        className="py-10 md:py-16 px-6 md:px-12"
        style={{ backgroundColor: "#F5F5F5" }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-semibold text-foreground mb-4">
              {t("What we do?")}
            </h2>
            <p className="text-textColor text-base md:text-md mt-2 max-w-xl mx-auto font-light">
              {t(
                "Empowering local stores and simplifying consumer shopping through cutting-edge technology.",
              )}
            </p>
          </div>

          <div className="space-y-8 px-0 md:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-8 items-center">
              {/* IMAGE - should come first on mobile */}
              <div className="flex justify-end order-1 lg:order-2">
                {/* <img
                  src={Business}
                  alt="Business professional using mobile technology"
                  className="w-full object-cover"
                /> */}
                <Image
                  src={require("../../assets/AboutUs/Business.webp")}
                  alt="Business professional using mobile technology"
                  className="w-full object-cover"
                  preview={false}
                />
              </div>

              {/* CONTENT - should come second on mobile */}
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm flex flex-col justify-center h-full order-2 lg:order-1">
                <h3 className="text-2xl md:text-2xl font-medium text-foreground mb-4">
                  {t("For Businesses (B2B)")}
                </h3>
                <p className="text-textColor leading-relaxed font-light text-md">
                  {t(
                    "We develop tailored retail tech solutions that help small and medium businesses digitize operations, boost efficiency, and remain competitive.",
                  )}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8 items-center">
              <div className="flex justify-start">
                {/* <img
                  src={Consumer}
                  alt="Business professional using mobile technology"
                  className="w-full object-cover"
                /> */}
                <Image
                  src={require("../../assets/AboutUs/Consumer.webp")}
                  alt="Business professional using mobile technology"
                  className="w-full object-cover"
                  preview={false}
                />
              </div>
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm flex flex-col justify-center h-full">
                <h3 className="text-2xl md:text-2xl font-medium text-foreground mb-4">
                  {t("For Consumers (B2C)")}
                </h3>
                <p className="text-textColor leading-relaxed font-light text-md">
                  {t(
                    "Our platforms provide an easier, more convenient shopping experience by connecting users to nearby local stores for faster delivery and better product discovery and price transparency.",
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:pt-16 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-semibold text-foreground mb-4">
              {t("The values that drive us")}
            </h2>
            <p className="text-textColor text-base md:text-md mt-2 max-w-xl mx-auto font-light">
              {t(
                "Empowering local stores and simplifying consumer shopping through cutting-edge technology.",
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {valuesData.map((item, index) => (
              <ValueCard
                key={index}
                img={item.img}
                title={t(item.title)}
                description={t(item.description)}
                gradientFrom={item.gradientFrom}
                gradientTo={item.gradientTo}
              />
            ))}
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:[grid-template-columns:1.5fr_1fr_1fr_1fr] gap-8">
            <div className="space-y-1text-sm text-muted-foreground">
              <div className="flex items-center space-x-2 mb-3">
                <Image
                  src={require("../../assets/Logo/logo.png")}
                  alt="myGINNE logo"
                  width={120}
                  height={40}
                  preview={false}
                />
              </div>
              <div
                className={`text-[12px] mb-1 ${
                  i18n.language === "ar" ? "text-right" : "text-left"
                }`}
                dir="ltr"
              >
                {t(
                  "14 Prince Waleed Bin-Thinyan Al-Saud, Sheraton Al Matar, El Nozha, Cairo Governorate 11799",
                )}
              </div>

              <div
                className={`flex items-center text-[12px] mb-1 ${
                  i18n.language === "ar" ? "justify-end" : "justify-start"
                }`}
                dir="ltr"
              >
                <PhoneCall
                  className={`${
                    i18n.language === "ar" ? "mr-2" : "mr-2"
                  } w-4 h-4 text-primary`}
                />
                +20 100 450 5666
              </div>

              <div
                className={`flex items-center text-[12px] mb-1 ${
                  i18n.language === "ar" ? "justify-end" : "justify-start"
                }`}
                dir="ltr"
              >
                <Mail
                  className={`${
                    i18n.language === "ar" ? "mr-2" : "mr-2"
                  } w-4 h-4 text-primary`}
                />
                support@myginne.com
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-foreground">
                {t("Company")}
              </h4>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  <a
                    href="https://myginne.com/"
                    className="hover:text-primary transition-colors"
                    target="blank"
                  >
                    {t("About Us")}
                  </a>
                </p>
                <p>
                  <a
                    href="https://myginne.com/privacy-policy.html"
                    className="hover:text-primary transition-colors"
                    target="blank"
                  >
                    {t("Privacy Policy")}
                  </a>
                </p>
                <p>
                  <a
                    href="https://myginne.com/refund-and-cancellation-policy"
                    target="blank"
                    className="hover:text-primary transition-colors"
                  >
                    {t("Refund & Cancellation Policy")}
                  </a>
                </p>
                <p>
                  <a
                    href="https://myginne.com/shipping-and-delivery-policy"
                    target="blank"
                    className="hover:text-primary transition-colors"
                  >
                    {t("Shipping & Delivery Policy")}
                  </a>
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-foreground">
                {t("Support")}
              </h4>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  <a
                    href="https://api.whatsapp.com/send/?phone=201004505666&text&type=phone_number&app_absent=0"
                    className="hover:text-primary transition-colors"
                    target="blank"
                  >
                    {t("WhatsApp Support")}
                  </a>
                </p>
                <p>
                  <button
                    onClick={openContactUsModal}
                    className="hover:text-primary transition-colors text-left"
                  >
                    {t("Contact Us")}
                  </button>
                </p>
                {/* <p>
                  <a href="#" className="hover:text-primary transition-colors">
                    {t("Training Videos")}
                  </a>
                </p> */}
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-foreground">
                {t("Download Apps")}
              </h4>
              <div className="space-y-3">
                <a
                  href="https://play.google.com/store/apps/details?id=com.myginne.com.storeapp&hl=en_IN"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button
                    variant="new"
                    className="w-full justify-start border-none"
                  >
                    {/* <Smartphone className="mr-2 w-4 h-4" /> */}
                    <Image
                      src={require("../../assets/Logo/StoreAppIcon.png")}
                      alt="myGINNE logo"
                      width={37}
                      height={37}
                      preview={false}
                      style={{ borderRadius: "8px" }}
                    />
                    {t("Store App")}
                  </Button>
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=com.myginne.app&hl=en_IN"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button
                    variant="new"
                    className="w-full justify-start border-none"
                  >
                    {/* <Smartphone className="mr-2 w-4 h-4" /> */}
                    <Image
                      src={require("../../assets/Logo/CashierAppIcon.webp")}
                      alt="myGINNE logo"
                      width={37}
                      height={37}
                      preview={false}
                      style={{ borderRadius: "8px" }}
                    />
                    {t("Cashier App")}
                  </Button>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-8 mt-8 text-center text-muted-foreground">
            <p>
              {t(
                "© 2026 myGINNE. All rights reserved. Made for Egyptian retailers with ❤️",
              )}
            </p>
          </div>
        </div>
      </footer>

      {/* Talk to Sales Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{modalTitle}</DialogTitle>
          </DialogHeader>
          <div className="relative min-h-[430px] overflow-hidden">
            <LeadFormComp
              formSubmit={formSubmit}
              // formSubmitDone={formSubmitDone}
              utms={utms}
            />
            {/* )} */}
          </div>
        </DialogContent>
      </Dialog>
      {/* <ContactForm /> */}
      {/* <Footer /> */}
    </div>
  );
};

export default AboutUs;
